/**
 * Login por COOKIE del sitio SharePoint (sin registro de app en Azure AD).
 *
 * Identifica al usuario con su sesión del propio sitio (`/_api/web/currentuser`,
 * que viaja con la cookie `same-origin`) y resuelve su ROL desde la lista
 * `PmisUsuarios` (por correo). Sirve tanto a usuarios internos de Tecpetrol
 * (entran por SSO) como a contratistas invitados como invitados B2B al sitio.
 *
 * Se activa cuando `VITE_BACKEND=sharepoint` y NO hay App Registration de MSAL
 * configurada (ver `AuthProvider`). Si más adelante se registra una app, MSAL
 * toma el control sin tocar este archivo.
 *
 * El `tenantId` del PMIS (etiqueta de datos, no un tenant de Microsoft) es un
 * valor de configuración fijo: `VITE_TENANT_ID`. Debe coincidir con el que
 * etiqueta los datos sembrados en las listas.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Spinner } from "@fluentui/react-components";
import type { Role } from "@constants/roles";
import { spGet } from "../data/adapters/sharepoint/spClient";
import { buildRepos } from "../data/repos";
import { AuthContext, type AuthUser, type AuthState } from "./useAuth";
import { normalizarCorreoInvitado } from "./spGuestEmail";
import { saveIdentity, loadIdentity, clearIdentity } from "../offline/identityCache";
import { purgarLocal } from "../offline/purgeLocal";
import { esErrorDeRed } from "../offline/connectivity";
import { DEPLOYMENT } from "@core/access/deployment";

const TENANT_SP = DEPLOYMENT.tenantId;

interface SpCurrentUser {
  Email?: string;
  Title?: string;
  LoginName?: string;
  UserPrincipalName?: string;
}

/** "offline" = fallo de red (usar caché); null = no autenticado (sesión expirada/sin acceso). */
type ResultadoSpUser = { email: string; name: string } | "offline" | null;

/** Lee el usuario actual del sitio vía la cookie de sesión. */
async function fetchCurrentSpUser(): Promise<ResultadoSpUser> {
  try {
    const u = await spGet<SpCurrentUser>(
      "web/currentuser?$select=Email,Title,LoginName,UserPrincipalName",
      "currentUser",
    );
    // Para invitados B2B, `Email` puede venir vacío y el correo real llega
    // adornado (#EXT#) en UserPrincipalName/LoginName → se reconstruye.
    const email = normalizarCorreoInvitado(u.Email || u.UserPrincipalName || u.LoginName);
    if (!email) return null;
    return { email, name: u.Title ?? email };
  } catch (e) {
    // Red → "offline" (usar caché). Auth/sesión expirada/otro → no autenticado (sin caché).
    return esErrorDeRed(e) ? "offline" : null;
  }
}

export function SpCookieAuthProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<{ cargando: boolean; user: AuthUser | null }>({
    cargando: true,
    user: null,
  });

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const spu = await fetchCurrentSpUser();
      if (!vivo) return;
      if (spu === "offline") {
        // Sin red: usar la identidad cacheada (modo offline) si existe (Pieza A).
        setEstado({ cargando: false, user: loadIdentity(TENANT_SP) });
        return;
      }
      if (!spu) {
        // No autenticado (sesión expirada / sin acceso): NO usar el caché.
        setEstado({ cargando: false, user: null });
        return;
      }
      // Resuelve el rol desde PmisUsuarios (comparación de correo case-insensitive).
      let role: Role | null = null;
      let exito = false;
      let fueRed = false;
      try {
        const usuarios = await buildRepos(TENANT_SP).usuario.list();
        const u = usuarios.find((x) => x.email.toLowerCase() === spu.email);
        role = u?.rol ?? null;
        exito = true; // la consulta respondió (con o sin match)
      } catch (e) {
        fueRed = esErrorDeRed(e);
      }
      if (exito) {
        const user: AuthUser = { email: spu.email, name: spu.name, role, tenantId: TENANT_SP };
        saveIdentity(user); // cachear solo una identidad con rol resuelto
        setEstado({ cargando: false, user });
      } else if (fueRed) {
        // Red intermitente: usar el caché previo (conserva el rol bueno) sin persistir.
        const cached = loadIdentity(TENANT_SP);
        setEstado({
          cargando: false,
          user: cached ?? { email: spu.email, name: spu.name, role: null, tenantId: TENANT_SP },
        });
      } else {
        // Error de negocio/servidor leyendo PmisUsuarios: entra sin rol, sin persistir.
        setEstado({
          cargando: false,
          user: { email: spu.email, name: spu.name, role: null, tenantId: TENANT_SP },
        });
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  if (estado.cargando) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <Spinner label="Conectando con SharePoint…" />
      </div>
    );
  }

  const value: AuthState = {
    user: estado.user,
    isAuthenticated: estado.user !== null,
    // En modo cookie no hay popup de login: re-intentar = recargar (re-lee la sesión).
    login: () => {
      window.location.reload();
      return Promise.resolve();
    },
    logout: () => {
      clearIdentity(TENANT_SP);
      // Limpia datos locales (Dexie + cachés SW) antes de salir — clave en el
      // equipo de un contratista externo.
      void (async () => {
        await purgarLocal();
        window.location.href = "/_layouts/15/SignOut.aspx";
      })();
      return Promise.resolve();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
